import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PinValidationService } from '../services/pin-validation.service';
import { PinAuthContext, PinResendSessionError, SecurityService } from '../services/security.service';
import { WebService } from '../services/web/web.service';
import { SpinnerLease, SpinnerService } from '../services/spinner/spinner.service';
import { ToastService } from '../services/notification/toast.service';
import { LoggerService } from '../services/logger.service';
import { Router } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InactivityMonitorService } from '../services/inactivity-monitor.service';
import { WebAuthnService } from '../services/webauthn.service';
import { PasskeyService } from '../services/passkey.service';
import { PasskeyErrorHandlerService } from '../services/passkey-error-handler.service';
import { PasskeyAvailabilityState } from '../models/passkey.model';
import { PinValidationResponse } from '../models/pin-validation.model';

/**
 * Componente modal para validar PIN durante la renovación del refresh token
 * Proporciona una capa adicional de seguridad al solicitar validación por email
 */
@Component({
  selector: 'app-pin-validation-modal',
  templateUrl: './pin-validation-modal.component.html',
  styleUrls: ['./pin-validation-modal.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule]
})
export class PinValidationModalComponent implements OnInit, OnDestroy {
  
  validateForm: FormGroup;
  showModal = false;
  private subscription: Subscription = new Subscription();
  private logger: LoggerService;
  
  showPin = false;
  helpMessage = '';
  failedAttempts = 0;
  maxAttempts = 3;

  // ═══════════════════════════════════════════════════════════════════
  // PROPIEDADES PARA PASSKEY
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * Indica si se debe mostrar la opción de validación con Passkey.
   */
  showPasskeyOption: boolean = false;

  /**
   * Indica si se está procesando una validación con Passkey.
   */
  isLoadingPasskey: boolean = false;

  /**
   * Mensaje de error si falla la validación con Passkey.
   */
  passkeyError: string | null = null;

  /**
   * Nombre del método biométrico disponible.
   */
  biometricMethodName: string = 'Passkey';

  /**
   * Indica si el navegador soporta WebAuthn.
   */
  webAuthnSupported: boolean = false;

  /**
   * Indica si se está verificando la disponibilidad de passkey.
   * Se usa para mostrar un estado de carga inicial en el modal.
   */
  isCheckingPasskey: boolean = false;

  /**
   * Indica qué método de validación está activo.
   * 'passkey' = Muestra solo la opción de passkey
   * 'pin' = Muestra solo el formulario de PIN
   */
  activeMethod: 'passkey' | 'pin' = 'passkey';
  passkeyAvailabilityState: PasskeyAvailabilityState = 'idle';
  pinExplicitlySelected = false;
  isSubmittingPin = false;
  isResendingPin = false;
  private passkeyAbortController: AbortController | null = null;
  private passkeyGeneration = 0;
  private passkeyLookupAbortController: AbortController | null = null;
  private passkeyLookupGeneration = 0;
  private modalGeneration = 0;
  private pinSubscription: Subscription | null = null;
  private pinLease: SpinnerLease | null = null;
  private pinGeneration = 0;
  private pinFailureTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly passkeyOperationTimeoutMs = 90000;

  constructor(
    private pinValidationService: PinValidationService,
    private securityService: SecurityService,
    private webService: WebService,
    private spinner: SpinnerService,
    private toast: ToastService,
    private router: Router,
    private inactivityMonitor: InactivityMonitorService,
    private webAuthnService: WebAuthnService,
    private passkeyService: PasskeyService,
    private passkeyErrorHandler: PasskeyErrorHandlerService
  ) {
    this.logger = new LoggerService();
    
    this.validateForm = new FormGroup({
      pin: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(8)]),
      email: new FormControl(''),
      partner: new FormControl('')
    });
  }

  ngOnInit(): void {
    const authContextChanges$ = (this.securityService as any).authContextChanges$;
    if (authContextChanges$) {
      this.subscription.add(authContextChanges$.subscribe(() => this.cancelAuthContextOperations()));
    }
    this.subscription.add(
      this.pinValidationService.showPinModal$.subscribe(async show => {
        const generation = ++this.modalGeneration;
        this.showModal = show;
        
        if (show) {
          this.logger.log('�� [PIN-MODAL] Modal mostrado - Verificando passkey...');
          
          // Resetear formulario y mensajes
          this.validateForm.reset();
          this.helpMessage = '';
          this.passkeyError = null;
          this.pinExplicitlySelected = false;
          this.pinValidationService.resetPinSentFlag();
          
          // NUEVO: Verificar si tiene passkey
          await this.checkPasskeyAvailability();
          if (!this.isCurrentModalOpening(generation)) return;
          
          if (this.shouldAutomaticallySendPin()) {
            this.logger.log('📧 [PIN-MODAL] Fallback PIN válido - Enviando PIN automáticamente');
            this.resendPin();
          } else if (this.passkeyAvailabilityState === 'available') {
            this.logger.log('🔐 [PIN-MODAL] Usuario tiene passkey - NO enviando PIN automáticamente');
            this.logger.log('ℹ️ [PIN-MODAL] Usuario puede solicitar PIN manualmente si lo prefiere');
          }
        } else {
          this.cancelPinOperation();
          this.cancelPasskeyOperation();
          this.cancelPasskeyLookup();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.modalGeneration++;
    this.showModal = false;
    this.cancelPinOperation();
    this.cancelPasskeyOperation();
    this.cancelPasskeyLookup();
    this.subscription.unsubscribe();
  }

  validate(): void {
    if (this.validateForm.invalid || this.isSubmittingPin || this.pinSubscription) {
      this.toast.showError('Por favor ingrese un PIN válido de 8 dígitos');
      return;
    }

    const pinValue = this.validateForm.get('pin')?.value;
    const generation = this.startPinOperation();
    const context = this.capturePinContext();
    this.isSubmittingPin = true;
    const lease = this.spinner.acquire();
    this.pinLease = lease;
    const request = this.securityService.validatePinForCurrentSession(pinValue).pipe(finalize(() => {
      lease.release();
      if (!this.isCurrentPinOperation(generation, context)) return;
      this.isSubmittingPin = false;
      if (this.pinLease === lease) this.pinLease = null;
      this.pinSubscription = null;
    })).subscribe(
      (res: PinValidationResponse) => {
        if (!this.isCurrentPinOperation(generation, context)) return;
        if (res.valid !== true) {
          if (this.isSessionInvalid(res)) {
            this.restartAuthentication();
            return;
          }
          this.showPinValidationFailure(res, generation, context);
          this.securityService.isPinValidated(false);
          return;
        }
        
        this.pinValidationService.resetFailedAttempts();
        this.helpMessage = '';
        
        // Marcar PIN como validado
        this.securityService.isPinValidated(true);
        
        // Reiniciar actividad
        this.inactivityMonitor.markPinValidated();
        
        this.pinValidationService.notifyPinValidated();
        
        this.toast.showSuccess('PIN validado correctamente. Puede continuar usando la aplicación.');
      },
      (error: unknown) => {
        if (!this.isCurrentPinOperation(generation, context)) return;
        if (this.isSessionInvalid(error)) {
          this.restartAuthentication();
          return;
        }
        this.showPinValidationFailure(error, generation, context);
        this.securityService.isPinValidated(false);
      }
    );
    this.pinSubscription = request;
    if (request.closed) this.pinSubscription = null;
  }

  resendPin(): void {
    if (this.isResendingPin || this.pinSubscription) return;
    const generation = this.startPinOperation();
    const context = this.capturePinContext();
    this.isResendingPin = true;
    const lease = this.spinner.acquire();
    this.pinLease = lease;
    const request = this.securityService.resendPinForCurrentSession().pipe(finalize(() => {
      lease.release();
      if (!this.isCurrentPinOperation(generation, context)) return;
      this.isResendingPin = false;
      if (this.pinLease === lease) this.pinLease = null;
      this.pinSubscription = null;
    })).subscribe({
      next: () => {
        if (!this.isCurrentPinOperation(generation, context)) return;
        this.logger.log('✅ [PIN-MODAL] PIN enviado exitosamente al correo del usuario');
        this.pinValidationService.markPinAsSent();
        this.toast.showSuccess('PIN enviado a tu correo electrónico');
      },
      error: (error: unknown) => {
        if (!this.isCurrentPinOperation(generation, context)) return;
        this.logger.error('❌ [PIN-MODAL] Error al enviar PIN:', error);
        this.pinValidationService.resetPinSentFlag();

        if (this.isSessionInvalid(error)) {
          this.restartAuthentication();
          return;
        }

        this.toast.showError('Error al enviar el PIN. Por favor use el botón "Reenviar Código".');
      }
    });
    this.pinSubscription = request;
    if (request.closed) this.pinSubscription = null;
  }

  logout(): void {
    this.cancelPinOperation();
    this.cancelPasskeyOperation();
    this.pinValidationService.hidePinModal();
    this.securityService.logout();
  }

  toggleShowPin(): void {
    this.showPin = !this.showPin;
  }

  onEnterKey(event: Event): void {
    event.preventDefault();
    if (this.validateForm.valid) {
      this.validate();
    }
  }

  /**
   * Cambia al método de validación por PIN.
   * Oculta la opción de passkey y muestra el formulario de PIN.
   * Envía el PIN automáticamente al correo del usuario.
   */
  switchToPinMethod(): void {
    this.cancelPinOperation();
    this.cancelPasskeyOperation();
    this.cancelPasskeyLookup();
    this.logger.log('🔄 [PIN-MODAL] Usuario eligió validar con PIN');
    this.activeMethod = 'pin';
    this.pinExplicitlySelected = true;
    
    // Enviar PIN automáticamente
    this.resendPin();
  }

  /**
   * Cambia al método de validación por Passkey.
   * Oculta el formulario de PIN y muestra la opción de passkey.
   */
  switchToPasskeyMethod(): void {
    this.cancelPinOperation();
    this.cancelPasskeyLookup();
    this.logger.log('🔄 [PIN-MODAL] Usuario eligió validar con Passkey');
    this.activeMethod = 'passkey';
    
    // Limpiar errores previos
    this.passkeyError = null;
    this.helpMessage = '';
  }

  async retryPasskeyLookup(): Promise<void> {
    if (this.isCheckingPasskey || !this.showModal) return;
    const modalGeneration = this.modalGeneration;
    const email = this.securityService.getTokenSub();
    const context = email ? this.capturePasskeyLookupContext(email) : null;
    this.passkeyError = null;
    await this.checkPasskeyAvailability();
    if (!this.isCurrentModalRetry(modalGeneration, context)) return;
    if (this.shouldAutomaticallySendPin()) this.resendPin();
  }

  // ═══════════════════════════════════════════════════════════════════
  // MÉTODOS PARA PASSKEY
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Verifica si el usuario tiene passkey registrada.
   * 
   * Se ejecuta cuando se muestra el modal para determinar
   * si mostrar la opción de validación rápida con passkey.
   */
  private async checkPasskeyAvailability(): Promise<void> {
    this.logger.log('🔍 [PIN-MODAL] Verificando disponibilidad de passkey...');
    this.cancelPasskeyLookup();
    this.isCheckingPasskey = true;
    this.passkeyAvailabilityState = 'checking';
    this.showPasskeyOption = false;
    
    // Verificar soporte de WebAuthn
    this.webAuthnSupported = this.webAuthnService.isWebAuthnSupported();
    
    if (!this.webAuthnSupported) {
      this.logger.warn('⚠️ [PIN-MODAL] WebAuthn no soportado');
      this.passkeyAvailabilityState = 'idle';
      this.isCheckingPasskey = false;
      return;
    }

    // Obtener email del usuario
    const email = this.securityService.getTokenSub();

    if (!email) {
      this.logger.warn('⚠️ [PIN-MODAL] No se pudo obtener email del usuario');
      this.passkeyAvailabilityState = 'idle';
      this.isCheckingPasskey = false;
      return;
    }

    const context = this.capturePasskeyLookupContext(email);
    const operation = this.startPasskeyLookup();
    const generation = this.passkeyLookupGeneration;
    try {
      this.logger.log('📡 [PIN-MODAL] Verificando passkey para email:', email);
      const hasPasskey = await this.passkeyService.hasPasskey(context.email ?? undefined, context.partner ?? undefined, operation);
      if (!this.isCurrentPasskeyLookup(generation, context)) return;
      
      if (hasPasskey) {
        this.passkeyAvailabilityState = 'available';
        this.logger.log('✅ [PIN-MODAL] Usuario tiene passkey - Inicializando opción de validación rápida');
        this.showPasskeyOption = true;
        
        // Obtener nombre del método biométrico
        this.biometricMethodName = await this.webAuthnService.getBiometricMethodName();
        if (!this.isCurrentPasskeyLookup(generation, context)) return;
        this.logger.log('✅ [PIN-MODAL] Método biométrico:', this.biometricMethodName);
      } else {
        this.passkeyAvailabilityState = 'absent';
        this.logger.log('ℹ️ [PIN-MODAL] Usuario NO tiene passkey');
      }
    } catch (error) {
      if (!this.isCurrentPasskeyLookup(generation, context)) return;
      this.logger.error('❌ [PIN-MODAL] Error verificando passkey:', error);
      this.passkeyAvailabilityState = 'error';
      this.pinValidationService.resetPinSentFlag();
    } finally {
      if (this.isCurrentPasskeyLookup(generation, context)) {
        this.isCheckingPasskey = false;
        this.passkeyLookupAbortController = null;
        this.logger.log('✅ [PIN-MODAL] Verificación de passkey completada');
      }
    }
  }

  /**
   * Ejecuta la re-validación con Passkey.
   * 
   * Este método reemplaza la validación de PIN por email con una
   * validación biométrica instantánea.
   * 
   * Flujo:
   * 1. Llama al PasskeyService para re-validar
   * 2. Marca PIN como validado
   * 3. Reinicia el registro de actividad
   * 4. Cierra el modal
   * 5. Usuario continúa en la app
   */
  async validateWithPasskey(): Promise<void> {
    this.logger.log('🔐 [PIN-MODAL] ========================================');
    this.logger.log('🔐 [PIN-MODAL] Iniciando re-validación con Passkey');
    
    if (this.isLoadingPasskey) return;
    this.cancelPasskeyLookup();
    const operation = this.startPasskeyOperation();
    const generation = this.passkeyGeneration;
    const context = this.capturePinContext();
    this.isLoadingPasskey = true;
    this.passkeyError = null;

    try {
      this.logger.log('📡 [PIN-MODAL] Llamando a PasskeyService.revalidateWithPasskey()...');
      
      // Ejecutar re-validación con passkey
      const success = await this.passkeyService.revalidateWithPasskey(operation);
      if (!this.isCurrentPasskeyOperation(generation, context)) return;

      if (!success) {
        throw new Error('Re-validación fallida');
      }

      this.logger.log('✅ [PIN-MODAL] Re-validación exitosa');

      // Resetear intentos fallidos
      this.pinValidationService.resetFailedAttempts();
      this.helpMessage = '';
      
      // Marcar PIN como validado
      this.logger.log('✅ [PIN-MODAL] Marcando PIN como validado');
      this.securityService.isPinValidated(true);
      if (!this.isCurrentPasskeyOperation(generation, context)) return;
      
      // Reiniciar actividad
      this.logger.log('🔄 [PIN-MODAL] Reiniciando actividad');
      this.inactivityMonitor.markPinValidated();
      if (!this.isCurrentPasskeyOperation(generation, context)) return;
      
      // Mostrar mensaje de éxito
      this.toast.showSuccess('Identidad verificada con ' + this.biometricMethodName);
      
      this.logger.log('✅ [PIN-MODAL] Re-validación completada - Cerrando modal');
      
      // Notificar que el PIN fue validado (esto cierra el modal)
      this.pinValidationService.notifyPinValidated();

    } catch (error: unknown) {
      if (!this.isCurrentPasskeyOperation(generation, context)) return;
      this.logger.error('❌ [PIN-MODAL] Error en re-validación con passkey:', error);
      
      const result = this.passkeyErrorHandler.handlePasskeyError(error, 'revalidation');
      this.passkeyError = result.message;
      this.toast.showError(result.message);
    } finally {
      if (this.isCurrentPasskeyOperation(generation, context)) {
        this.isLoadingPasskey = false;
        this.passkeyAbortController = null;
      }
    }
  }

  private showPinValidationFailure(
    response: unknown,
    generation: number,
    context: { email: string | null; partner: number | null; sessionId: string | null; authRevision: number }
  ): void {
    const errorBody = response instanceof HttpErrorResponse && response.status === 401
      ? response.error
      : response;
    const failure = errorBody as (PinValidationResponse & { error?: PinValidationResponse }) | undefined;
    if ((failure?.code ?? failure?.error?.code) !== 'PIN_INVALID_OR_EXPIRED') {
      this.helpMessage = 'No pudimos validar el código. Inténtalo de nuevo o solicita otro PIN.';
      this.toast.showError(this.helpMessage);
      return;
    }
    this.pinValidationService.incrementFailedAttempts();
    const failedAttempts = this.pinValidationService.getFailedAttempts();
    const maxAttempts = this.pinValidationService.getMaxAttempts();
    if (this.pinValidationService.hasReachedMaxAttempts()) {
      this.helpMessage = `Has alcanzado el máximo de intentos (${maxAttempts}). Por seguridad, cerraremos tu sesión.`;
      this.toast.showError('Máximo de intentos alcanzado. Cerrando sesión por seguridad...');
      this.clearPinFailureTimer();
      this.pinFailureTimer = setTimeout(() => {
        if (!this.isCurrentPinOperation(generation, context)) return;
        this.pinValidationService.hidePinModal();
        this.securityService.logout();
      }, 2000);
      return;
    }
    const remainingAttempts = maxAttempts - failedAttempts;
    this.helpMessage = `PIN inválido. Te quedan ${remainingAttempts} intento${remainingAttempts > 1 ? 's' : ''}.`;
    this.toast.showError(this.helpMessage);
  }

  get showPinForm(): boolean {
    return this.pinExplicitlySelected || this.passkeyAvailabilityState === 'absent' || this.passkeyAvailabilityState === 'idle';
  }

  get pinWasSent(): boolean {
    return this.pinValidationService.wasPinSentInCurrentSession();
  }

  private shouldAutomaticallySendPin(): boolean {
    return this.showModal && (this.passkeyAvailabilityState === 'absent' ||
      !this.webAuthnSupported ||
      !this.securityService.getTokenSub());
  }

  private isCurrentModalOpening(generation: number): boolean {
    return this.showModal && generation === this.modalGeneration;
  }

  private isCurrentModalRetry(
    generation: number,
    context: PinAuthContext | null
  ): boolean {
    return this.isCurrentModalOpening(generation) &&
      (context ? this.isCurrentPasskeyLookupContext(context) : !this.securityService.getTokenSub());
  }

  private isSessionInvalid(response: unknown): boolean {
    return response instanceof PinResendSessionError;
  }

  private restartAuthentication(): void {
    this.cancelPinOperation();
    this.cancelPasskeyOperation();
    this.cancelPasskeyLookup();
    this.helpMessage = 'Tu sesión expiró. Inicia sesión nuevamente para solicitar un código nuevo.';
    this.toast.showError(this.helpMessage);
    this.securityService.isPinValidated(false);
    this.pinValidationService.resetPinSentFlag();
    this.pinValidationService.resetFailedAttempts();
    this.pinValidationService.hidePinModal();
    this.securityService.logout();
  }

  private startPasskeyOperation(): { signal: AbortSignal; deadlineAt: number } {
    this.cancelPasskeyLookup();
    this.cancelPasskeyOperation();
    this.passkeyAbortController = new AbortController();
    this.passkeyGeneration++;
    return { signal: this.passkeyAbortController.signal, deadlineAt: Date.now() + this.passkeyOperationTimeoutMs };
  }

  private cancelPasskeyOperation(): void {
    this.passkeyGeneration++;
    this.passkeyAbortController?.abort();
    this.passkeyAbortController = null;
    this.isLoadingPasskey = false;
  }

  private cancelPinOperation(): void {
    this.pinGeneration++;
    this.pinSubscription?.unsubscribe();
    this.pinSubscription = null;
    this.isSubmittingPin = false;
    this.isResendingPin = false;
    this.pinLease?.release();
    this.pinLease = null;
    this.clearPinFailureTimer();
  }

  private startPinOperation(): number {
    return ++this.pinGeneration;
  }

  private capturePinContext(): PinAuthContext {
    const authContext = (this.securityService as any).capturePinAuthContext?.();
    if (authContext) return authContext;
    const payload = this.securityService.getDecodedToken?.()?.payload;
    return {
      email: this.securityService.getTokenSub(),
      partner: null,
      sessionId: payload?.session_id ?? null,
      authRevision: 0
    };
  }

  private isCurrentPinOperation(
    generation: number,
    context: { email: string | null; partner: number | null; sessionId: string | null; authRevision: number }
  ): boolean {
    const current = this.capturePinContext();
    return generation === this.pinGeneration && this.isSamePinAuthContext(context, current);
  }

  private isSamePinAuthContext(left: PinAuthContext, right: PinAuthContext): boolean {
    return left.email === right.email &&
      left.partner === right.partner &&
      left.sessionId === right.sessionId &&
      left.authRevision === right.authRevision;
  }

  private clearPinFailureTimer(): void {
    if (this.pinFailureTimer !== null) {
      clearTimeout(this.pinFailureTimer);
      this.pinFailureTimer = null;
    }
  }

  private isCurrentPasskeyOperation(generation: number, context: PinAuthContext): boolean {
    return generation === this.passkeyGeneration &&
      !this.passkeyAbortController?.signal.aborted &&
      this.isSamePinAuthContext(context, this.capturePinContext());
  }

  private startPasskeyLookup(): { signal: AbortSignal; deadlineAt: number } {
    this.cancelPasskeyLookup();
    this.passkeyLookupAbortController = new AbortController();
    this.passkeyLookupGeneration++;
    return {
      signal: this.passkeyLookupAbortController.signal,
      deadlineAt: Date.now() + this.passkeyOperationTimeoutMs
    };
  }

  private cancelPasskeyLookup(): void {
    this.passkeyLookupGeneration++;
    this.passkeyLookupAbortController?.abort();
    this.passkeyLookupAbortController = null;
    this.isCheckingPasskey = false;
  }

  private capturePasskeyLookupContext(email: string): PinAuthContext {
    return { ...this.capturePinContext(), email };
  }

  private isCurrentPasskeyLookup(
    generation: number,
    context: PinAuthContext
  ): boolean {
    return this.showModal &&
      generation === this.passkeyLookupGeneration &&
      !this.passkeyLookupAbortController?.signal.aborted &&
      this.isCurrentPasskeyLookupContext(context);
  }

  private isCurrentPasskeyLookupContext(
    context: PinAuthContext
  ): boolean {
    const email = this.securityService.getTokenSub();
    return !!email && this.isSamePinAuthContext(context, this.capturePasskeyLookupContext(email));
  }

  private cancelAuthContextOperations(): void {
    this.cancelPinOperation();
    this.cancelPasskeyOperation();
    this.cancelPasskeyLookup();
  }
}
